import { supabase } from '@/lib/supabase';
import { businessService } from './business';
import { cleanAddressForGeocoding } from '@/lib/utils';
import { naverMapService } from './naverMap';
import type { RestaurantWithProducts, RestaurantCreate, RestaurantUpdate } from './restaurant.types';

export class RestaurantCrudService {
  
  async createRestaurant(
    restaurantData: RestaurantCreate
  ): Promise<{ success: boolean; data?: RestaurantWithProducts; error?: string }> {
    try {
      // 주소를 기반으로 좌표 추가
      if (restaurantData.address && (!restaurantData.latitude || !restaurantData.longitude)) {
        const cleanedAddress = cleanAddressForGeocoding(restaurantData.address);
        const result = await naverMapService.geocode(cleanedAddress);
        if (result.success && (result as any).y && (result as any).x) {
          restaurantData.latitude = (result as any).y;
          restaurantData.longitude = (result as any).x;
        }
      }

      // If business_id is provided, verify it exists
      if (restaurantData.business_id) {
        const business = await businessService.getBusinessById(restaurantData.business_id);
        if (!business) {
          return { success: false, error: '유효하지 않은 사업자 정보입니다.' };
        }
      }

      const { data, error } = await supabase
        .from('restaurants')
        .insert({
          name: restaurantData.name,
          bestmenu: restaurantData.bestmenu,
          address: restaurantData.address,
          phone: restaurantData.phone,
          latitude: restaurantData.latitude,
          longitude: restaurantData.longitude,
          owner_id: restaurantData.owner_id,
          business_id: restaurantData.business_id,
          settlement_bank_name: restaurantData.settlement_bank_name,
          settlement_account_number: restaurantData.settlement_account_number,
          settlement_account_holder: restaurantData.settlement_account_holder,
          image_url: restaurantData.image_url,
          store_type: restaurantData.store_type || 'korean',
          is_open: false, // Start closed
          is_new: true,
          rating: 0,
          total_reviews: 0
        })
        .select()
        .single();

      if (error) {
        console.error('Create restaurant error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Create restaurant error:', error);
      return { success: false, error: '가게 등록 중 오류가 발생했습니다.' };
    }
  }

  async updateRestaurant(
    id: string,
    updates: RestaurantUpdate
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('restaurants')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        console.error('Update restaurant error:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Update restaurant error:', error);
      return { success: false, error: '가게 정보 업데이트 중 오류가 발생했습니다.' };
    }
  }

  async toggleRestaurantStatus(
    id: string,
    isOpen: boolean
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('restaurants')
        .update({ 
          is_open: isOpen,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) {
        console.error('Toggle restaurant status error:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Toggle restaurant status error:', error);
      return { success: false, error: '가게 상태 변경 중 오류가 발생했습니다.' };
    }
  }

  async getRestaurantsByBusiness(businessId: string): Promise<RestaurantWithProducts[]> {
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Get restaurants by business error:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Get restaurants by business error:', error);
      return [];
    }
  }
}

export const restaurantCrudService = new RestaurantCrudService();